import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { EmailStatuses, SearchEmailFilter, getEmail, searchEmails } from 'core'

import Card from 'common/components/dataDisplay/Card'

import { DefaultInputV2, DefaultSelectV2, EmailsTable } from '../../common/components'
import Button from '../../common/components/general/Button'
import Icon from '../../common/components/general/Icon'
import { useQueryRequest } from '../../common/hooks'
import EmailContentForm from './EmailContentForm'

export const SEARCH_EMAILS_PATH = 'search-emails'

const SearchEmails: React.FC = () => {
  const { t: translate } = useTranslation()

  const [searchFilter, setSearchFilter] = useState<SearchEmailFilter>({
    pageNumber: 1,
    pageSize: 25,
    orderDescending: true,
    orderColumn: 'InitiatedOn',
    q: null,
    status: null,
  })

  const [showEmail, setShowEmail] = useState<boolean>(false)
  const [emails, isEmailsLoading, sendGetEmails] = useQueryRequest(null)
  const [emailContent, isEmailContentLoading, sendGetEmailContent] = useQueryRequest(null)

  useEffect(() => {
    ;(async () => {
      await sendGetEmails(() => searchEmails(searchFilter))
    })()
  }, [searchFilter])

  const onDisplay = async (id: string) => {
    await sendGetEmailContent(() => getEmail(id))
    setShowEmail(true)
  }

  const availableStatuses = [
    { name: 'All statuses', id: null },
    ...EmailStatuses.map((x) => ({ name: x.name, id: x.id })),
  ]

  return (
    <>
      <Row>
        <Col span={24}>
          <Card title={translate('csi-dashboard.email-search.title')}>
            <Formik
              enableReinitialize={true}
              validationSchema={Yup.object({})}
              initialValues={searchFilter}
              onSubmit={(values) => {
                setSearchFilter({
                  ...searchFilter,
                  status: values.status,
                  q: values.q,
                })
              }}
            >
              {(formik) => (
                <form onSubmit={formik.handleSubmit}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <DefaultInputV2
                        suffix={<Icon type="fal fa-search" fontAwesome />}
                        onKeyUp={async (e: any) => {
                          if (e.keyCode == 13) {
                            await formik.submitForm()
                          }
                        }}
                        className="mr-8"
                        placeholder={translate('csi-dashboard.email-search.input-placeholder')}
                        name={'q'}
                      />
                    </Col>
                    <Col span={4}>
                      <DefaultSelectV2
                        name="status"
                        placeholder={'Select status'}
                        label={null}
                        items={availableStatuses}
                      ></DefaultSelectV2>
                    </Col>
                    <Col span={8}>
                      <Button
                        type="primary"
                        className={'mr-16'}
                        htmlType={'submit'}
                        disabled={isEmailsLoading}
                        loading={isEmailsLoading}
                      >
                        {translate('general.search')}
                      </Button>

                      <Button
                        htmlType={'button'}
                        onClick={() => {
                          setSearchFilter({
                            ...searchFilter,
                            q: null,
                            status: null,
                          })
                        }}
                        disabled={isEmailsLoading}
                        loading={isEmailsLoading}
                      >
                        Reset Filter
                      </Button>
                    </Col>
                  </Row>
                </form>
              )}
            </Formik>

            <Row className="mt-16">
              <Col span={24}>
                <h5>
                  {emails?.totalItemCount} {translate('general.search-result-text')}
                </h5>
              </Col>
              <Col span={24}>
                <EmailsTable
                  showAction
                  onChange={(filter) => {
                    setSearchFilter({
                      ...searchFilter,
                      orderDescending: filter.orderDescending,
                      pageSize: filter.pageSize,
                      orderColumn: filter.orderColumn,
                      pageNumber: filter.pageNumber,
                    })
                  }}
                  emails={emails}
                  loadingEmails={isEmailsLoading}
                  actionIsLoading={isEmailContentLoading}
                  onActionClick={async (id) => await onDisplay(id)}
                  onRow={(id) => ({ onDoubleClick: async () => await onDisplay(id) })}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {emailContent && (
        <EmailContentForm
          visible={showEmail}
          content={emailContent}
          onClose={() => {
            setShowEmail(false)
          }}
        ></EmailContentForm>
      )}
    </>
  )
}

export default SearchEmails
